<template>
  <v-list two-line dense class="elevation-1 rounded-lg">
    <v-list-item
      v-for="(mod, i) in normalized"
      :key="mod.id || i"
      class="px-2"
    >
      <v-list-item-content class="py-0">
        <v-list-group
          no-action
          sub-group
          :class="{'text-transparent': !(mod.children && mod.children.length)}"
          :prepend-icon="mod.children && mod.children.length ? 'mdi-chevron-right' : 'mdi-circle-small-white'"
          :disabled="!(mod.children && mod.children.length)"
          :value="(mod.children && mod.children.length) ? openIds.has(mod.id) : false"
          @input="(mod.children && mod.children.length) && toggleOpen(mod.id, $event)"
        >
          <template #activator>
            <div class="d-flex align-top pb-2 w-100">
              <v-checkbox
                class="my-0 mr-3"
                hide-details
                :input-value="mod.enabled"
                @click.stop="onToggle(mod)"
              />
              <div class="flex-grow-1">
                <div class="font-weight-medium">
                  {{ (mod.name || '').toUpperCase() }}
                </div>
                <div class="text--secondary text-caption">
                  {{ mod.shortDescription }}
                </div>
              </div>
              <v-btn icon small @click.stop="openInfo(mod)">
                <v-icon small>mdi-information-outline</v-icon>
              </v-btn>
            </div>
          </template>

          <v-list dense class="pl-6">
            <v-list-item
              v-for="(child, rIndex) in mod.children"
              :key="child.id || rIndex"
              class="py-0"
            >
              <v-list-item-icon class="mr-2">
                <v-icon small class="text--secondary">mdi-circle-small-white</v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <div class="d-flex align-center">
                  <v-checkbox
                    class="my-0 py-0"
                    hide-details
                    :input-value="getChildChecked(child)"
                    @click.stop="onToggle(child)"
                  >
                    <template #label>
                      <span class="font-weight-medium">{{ child.name }}</span>
                      <v-btn icon small class="ml-1" @click.stop="openInfo(child, mod)">
                        <v-icon small>mdi-information-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-checkbox>
                </div>
                <div class="text--secondary text-caption pl-8">
                  {{ child.shortDescription }}
                </div>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-list-group>
      </v-list-item-content>
    </v-list-item>

    <v-dialog v-model="infoDialog" max-width="520">
      <v-card>
        <v-card-title class="headline">{{ infoTitle }}</v-card-title>
        <v-card-text>{{ infoDescription || 'No description provided.' }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="infoDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-list>
</template>

<script>
import { mapState } from 'vuex'
import BusinessRulesApi from '../../api/BusinessRulesApi' 

export default {
  name: 'ModuleRulesDropdown',
  data() {
    return {
      openIds: new Set(),
      infoDialog: false,
      infoTitle: '',
      infoDescription: '',
    }
  },
  computed: {
    ...mapState({
      adminModules: 'admin_modules',
      businessRules: 'business_rules',
    }),

    modulesById() {
      const m = new Map()
      ;(this.adminModules || []).forEach(x => m.set(x.id, x))
      return m
    },
    rulesById() {
      const r = new Map()
      ;(this.businessRules || []).forEach(x => r.set(x.id, x))
      return r
    },

    tree() {
      const modules = Array.isArray(this.adminModules) ? this.adminModules : []
      const rules   = Array.isArray(this.businessRules) ? this.businessRules : []

      const toModuleNode = (m) => ({
        id: m.id,
        parentId: m.parent_id ?? null,
        name: m.name || '',
        shortDescription: m.short_description || '',
        longDescription: m.long_description || '',
        enabled: m.enabled === 1 || m.enabled === true,
        type: 'module',
        children: [],
      })

      const toRuleNode = (r) => ({
        id: r.id,
        parentId: r.parent_id,
        name: r.name || '',
        shortDescription: r.short_description || '',
        longDescription: r.long_description || '',
        value: String(r.value) === '1' || r.value === true,
        dataType: r.data_type || 'boolean',
        type: 'rule',
        children: [],
      })

      const moduleNodes = modules.map(toModuleNode)
      const ruleNodes   = rules.map(toRuleNode)
      const modulesById = new Map(moduleNodes.map(n => [n.id, n]))

      const unmappedRules = []
      for (const r of ruleNodes) {
        const parentModule = modulesById.get(r.parentId)
        if (parentModule) parentModule.children.push(r)
        else unmappedRules.push(r)
      }

      const roots = []
      for (const m of moduleNodes) {
        if (m.parentId == null) {
          roots.push(m)
        } else {
          const parent = modulesById.get(m.parentId)
          if (parent) parent.children.push(m)
          else roots.push(m)
        }
      }

      const orderKey = (n) => (n.type === 'module' ? 0 : 1)
      const sortTree = (nodes) => {
        nodes.sort((a, b) => {
          const t = orderKey(a) - orderKey(b)
          return t !== 0 ? t : (a.name || '').localeCompare(b.name || '')
        })
        nodes.forEach(n => n.children && n.children.length && sortTree(n.children))
      }
      sortTree(roots)

      if (unmappedRules.length) {
        unmappedRules.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        roots.push({
          id: 'unmapped-rules',
          parentId: null,
          name: 'Unmapped Rules',
          shortDescription: 'Rules whose parent module was not found.',
          longDescription: '',
          type: 'group',
          children: unmappedRules,
        })
      }

      return roots
    },

    normalized() {
      return (this.tree || []).map((m) => {
        const children = Array.isArray(m.children)
          ? m.children.map((c) => ({
              id: c.id,
              name: c.name,
              type: c.type, 
              value: typeof c.value !== 'undefined' ? !!c.value : undefined,
              enabled: typeof c.enabled !== 'undefined' ? !!c.enabled : undefined,
              shortDescription: c.shortDescription || '',
              longDescription: c.longDescription || '',
            }))
          : []

        const allChecked = children.length > 0 && children.every((c) => this.getChildChecked(c))
        const someChecked = !allChecked && children.some((c) => this.getChildChecked(c))

        return {
          id: m.id,
          name: m.name,
          type: m.type || 'module',
          enabled: !!m.enabled,
          shortDescription: m.shortDescription || '',
          longDescription: m.longDescription || '',
          children,
          allChecked,
          someChecked,
        }
      })
    },
  },
  methods: {
    normalizeName(s) {
      return (s || '').replace(/\s+/g, ' ').trim().toLowerCase()
    },
    sameName(a, b) {
      return this.normalizeName(a) === this.normalizeName(b)
    },
    hasModule(item) {
      const id = item?.id
      const nm = this.normalizeName(item?.name)
      return (this.adminModules || []).some(m => m.id === id && this.sameName(m.name, nm))
    },
    hasRule(item) {
      const id = item?.id
      const nm = this.normalizeName(item?.name)
      return (this.businessRules || []).some(r => r.id === id && this.sameName(r.name, nm))
    },
    getModuleRaw(item) {
      const id = item?.id
      const nm = this.normalizeName(item?.name)
      return (this.adminModules || []).find(m => m.id === id && this.sameName(m.name, nm)) || null
    },
    getRuleRaw(item) {
      const id = item?.id
      const nm = this.normalizeName(item?.name)
      return (this.businessRules || []).find(r => r.id === id && this.sameName(r.name, nm)) || null
    },

    getChildChecked(child) {
      if (typeof child.enabled !== 'undefined') return !!child.enabled
      if (typeof child.value !== 'undefined') return !!child.value

      if (this.hasModule(child)) {
        const raw = this.getModuleRaw(child)
        return raw && (raw.enabled === 1 || raw.enabled === true)
      }
      if (this.hasRule(child)) {
        const raw = this.getRuleRaw(child)
        return raw && (String(raw.value) === '1' || raw.value === true)
      }
      if (this.modulesById.has(child.id)) {
        const raw = this.modulesById.get(child.id)
        return raw && (raw.enabled === 1 || raw.enabled === true)
      }
      if (this.rulesById.has(child.id)) {
        const raw = this.rulesById.get(child.id)
        return raw && (String(raw.value) === '1' || raw.value === true)
      }
      return false
    },

    toggleOpen(id, isOpen) {
      if (!id) return
      if (isOpen) this.openIds.add(id)
      else this.openIds.delete(id)
      this.openIds = new Set(this.openIds)
    },

    async onToggle(item) {
      if (!item || item.type === 'group' || item.id === 'unmapped-rules') return

      if (this.hasModule(item)) {
        await this.submitModule(item)
      } else if (this.hasRule(item)) {
        await this.changeBusinessRule(item)
      } else {
        if (typeof item.value !== 'undefined') {
          await this.changeBusinessRule(item)
        } else {
          await this.submitModule(item)
        }
      }
    },

    openInfo(target, parentModule) {
      const isModule = target && Array.isArray(target.children)
      this.infoTitle = isModule ? (target.name || 'Module') : (target.name || 'Rule')
      this.infoDescription = (target.longDescription || '').trim()
      this.infoDialog = true
    },

    async submitModule(item) {
      const raw = this.getModuleRaw(item)
      if (!raw) return
      const next = { ...raw, enabled: raw.enabled ? 0 : 1 }

      const updated = (this.adminModules || []).map(m =>
        m.id === raw.id ? { ...m, enabled: next.enabled } : m
      )
      this.$store.commit('storeAdminModules', updated)

      try {
        await this.$api?.adminModule?.store?.(next)
      } catch (e) {
      }
    },

    async changeBusinessRule(item) {
      const raw = this.getRuleRaw(item)
      if (!raw) return
      const next = {
        ...raw,
        value: (String(raw.value) === '1' || raw.value === true) ? 0 : 1,
      }

      try {
        const ok = await BusinessRulesApi.store(next)
        if (ok === true) {
          const fresh = await BusinessRulesApi.getList()
          this.$store.commit('storeBusinessRules', fresh)
        }
      } catch (e) {
      }
    },
  },
}
</script>
<style scoped>
  .empty-icon .v-list-group__header__prepend-icon {
    width: 24px; 
  }
</style>

